import type {
    AggregationSurveyReplyGuest,
    AggregationSurveySubmissionListItem
} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {DateTime} from "luxon";

type Translate = (key: string, options?: {[key: string]: unknown}) => string;

type ReplyStay = Pick<
    AggregationSurveySubmissionListItem,
    'stayStart' | 'stayEnd' | 'room' | 'mainGuests' | 'fellowGuests'
>;

// One formatter per fact, so the dialog, the list, the documents and the Excel file read a reply's stay alike.

/** The API fills the stay fields only when the reply's own booking is a hotel stay. */
export const hasReplyStay = (stay: ReplyStay) => !!(stay.stayStart || stay.stayEnd || stay.room);

export const replyStayNights = ({stayStart, stayEnd}: ReplyStay): number | undefined => {
    if (!stayStart || !stayEnd) return undefined;

    const nights = Math.round(DateTime.fromISO(stayEnd).diff(DateTime.fromISO(stayStart), 'days').days);
    return nights > 0 ? nights : undefined;
};

/**
 * Two calendar days (`YYYY-MM-DD`) as one short range: "23.–26.10.2026", "29.11.–01.12.2026",
 * "29.12.2026–02.01.2027". `withYear: false` leaves the year out while both days fall in the current one.
 */
export const formatDayRange = (start?: string | null, end?: string | null, {withYear = true} = {}) => {
    const from = start ? DateTime.fromISO(start) : undefined;
    const to = end ? DateTime.fromISO(end) : undefined;
    const fromValid = !!from?.isValid;
    const toValid = !!to?.isValid;
    if (!fromValid && !toValid) return '';
    if (!fromValid || !toValid) return (fromValid ? from! : to!).toFormat(withYear ? 'dd.MM.yyyy' : 'dd.MM.');

    if (from!.year !== to!.year) return `${from!.toFormat('dd.MM.yyyy')}–${to!.toFormat('dd.MM.yyyy')}`;

    const year = withYear || from!.year !== DateTime.now().year ? to!.toFormat('yyyy') : '';
    return from!.month === to!.month
        ? `${from!.toFormat('dd.')}–${to!.toFormat('dd.MM.')}${year}`
        : `${from!.toFormat('dd.MM.')}–${to!.toFormat('dd.MM.')}${year}`;
};

/** "23.–26.10.2026" */
export const formatReplyStayDates = ({stayStart, stayEnd}: ReplyStay) => formatDayRange(stayStart, stayEnd);

const namesOf = (guests?: AggregationSurveyReplyGuest[]) => (guests ?? [])
    .map(guest => guest.name?.trim())
    .filter((name): name is string => !!name);

/** The main guests' names, set only on a fellow guest's reply. */
export const replyMainGuestNames = ({mainGuests}: ReplyStay) => namesOf(mainGuests);

export const replyFellowGuestNames = ({fellowGuests}: ReplyStay) => namesOf(fellowGuests);

/** "Primary guest: Maria Hartmann" */
export const formatMainGuests = (stay: ReplyStay, t: Translate) => {
    const names = replyMainGuestNames(stay);
    return names.length ? `${t('Primary guest')}: ${names.join(', ')}` : '';
};

/** "With Manuela Walker" */
export const formatFellowGuests = (stay: ReplyStay, t: Translate) => {
    const names = replyFellowGuestNames(stay);
    return names.length ? t('With {{names}}', {names: names.join(', ')}) : '';
};
